import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import ImageUploader from "@/components/admin/ImageUploader";

const empty = {
  name_es: "", name_en: "", description_es: "", description_en: "",
  price: 0, category_id: "", images: [], stock: 5, active: true, featured: false,
};

export default function AdminProducts() {
  const { t } = useLang();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [p, c] = await Promise.all([api.get("/products"), api.get("/categories")]);
    setProducts(p.data);
    setCategories(c.data);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...empty, category_id: categories[0]?.id || "" });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editing, price: Number(editing.price), stock: Number(editing.stock) };
      if (editing.id) {
        await api.put(`/products/${editing.id}`, payload);
      } else {
        await api.post("/products", payload);
      }
      toast.success("Guardado");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    const prev = products;
    setProducts((ps) => ps.filter((p) => p.id !== id)); // actualización optimista
    try {
      await api.delete(`/products/${id}`);
      toast.success("Eliminado");
      await load(); // resincroniza con el servidor
    } catch (err) {
      setProducts(prev); // revierte si falla
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar");
    }
  };

  return (
    <div data-testid="admin-products-page">
      <header className="flex items-center justify-between mb-8">
        <div>
          <p className="tracking-eyebrow text-[var(--eafg-muted)]">Catálogo</p>
          <h1 className="font-serif-display text-5xl mt-2">{t("admin.products")}</h1>
        </div>
        <button
          onClick={openNew}
          className="bg-[var(--eafg-ink)] text-white px-5 py-3 tracking-eyebrow hover:bg-[var(--eafg-copper)] inline-flex items-center gap-2"
          data-testid="admin-new-product"
        >
          <Plus size={14} strokeWidth={1.5} /> {t("admin.newProduct")}
        </button>
      </header>

      <div className="border border-[var(--eafg-line)] bg-white">
        <table className="w-full text-left" data-testid="admin-products-table">
          <thead>
            <tr className="tracking-eyebrow text-[0.7rem] text-[var(--eafg-muted)] border-b border-[var(--eafg-line)]">
              <th className="p-4">Producto</th>
              <th className="p-4">Cat.</th>
              <th className="p-4 text-right">Precio</th>
              <th className="p-4 text-right">Stock</th>
              <th className="p-4 text-center">Activo</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => {
              const cat = categories.find((c) => c.id === p.category_id);
              return (
                <tr key={p.id} className="border-b border-[var(--eafg-line)] last:border-0">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {p.images?.[0] && (
                        <img src={p.images[0]} alt="" className="w-10 h-10 object-cover border border-[var(--eafg-line)]" />
                      )}
                      <span className="font-serif-display text-lg">{p.name_es}</span>
                    </div>
                  </td>
                  <td className="p-4 text-sm text-[var(--eafg-muted)]">{cat?.name_es || "—"}</td>
                  <td className="p-4 text-right tabular-nums">{p.price.toFixed(2)} €</td>
                  <td className="p-4 text-right tabular-nums">{p.stock}</td>
                  <td className="p-4 text-center">
                    <span className={`text-xs px-2 py-1 border ${p.active ? "border-[var(--eafg-copper)] text-[var(--eafg-copper)]" : "border-[var(--eafg-line)] text-[var(--eafg-muted)]"}`}>
                      {p.active ? "ON" : "OFF"}
                    </span>
                  </td>
                  <td className="p-4 text-right space-x-3">
                    <button onClick={() => setEditing(p)} data-testid={`admin-edit-product-${p.id}`}>
                      <Pencil size={14} strokeWidth={1.5} />
                    </button>
                    <button onClick={() => del(p.id)} data-testid={`admin-delete-product-${p.id}`}>
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {products.length === 0 && (
              <tr>
                <td colSpan={6} className="p-6 text-center text-[var(--eafg-muted)]">Sin productos.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-4" data-testid="product-modal">
          <form
            onSubmit={submit}
            className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8 space-y-5 border border-[var(--eafg-ink)]"
          >
            <div className="flex items-center justify-between">
              <h2 className="font-serif-display text-3xl">
                {editing.id ? t("admin.edit") : t("admin.newProduct")}
              </h2>
              <button type="button" onClick={() => setEditing(null)}>
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Nombre (ES)" value={editing.name_es} onChange={(v) => setEditing({ ...editing, name_es: v })} required testId="pf-name-es" />
              <Field label="Name (EN)" value={editing.name_en} onChange={(v) => setEditing({ ...editing, name_en: v })} required testId="pf-name-en" />
            </div>
            <Field label="Descripción (ES)" textarea value={editing.description_es} onChange={(v) => setEditing({ ...editing, description_es: v })} testId="pf-desc-es" />
            <Field label="Description (EN)" textarea value={editing.description_en} onChange={(v) => setEditing({ ...editing, description_en: v })} testId="pf-desc-en" />

            <div className="grid grid-cols-3 gap-4">
              <Field label="Precio (€)" type="number" step="0.01" value={editing.price} onChange={(v) => setEditing({ ...editing, price: v })} required testId="pf-price" />
              <Field label="Stock" type="number" value={editing.stock} onChange={(v) => setEditing({ ...editing, stock: v })} testId="pf-stock" />
              <label className="block">
                <span className="tracking-eyebrow text-[var(--eafg-muted)] block mb-2">Categoría</span>
                <select
                  value={editing.category_id}
                  onChange={(e) => setEditing({ ...editing, category_id: e.target.value })}
                  className="w-full border-b border-[var(--eafg-line)] py-2 bg-transparent"
                  data-testid="pf-category"
                >
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name_es}</option>
                  ))}
                </select>
              </label>
            </div>

            <ImageUploader
              images={editing.images || []}
              onChange={(imgs) => setEditing({ ...editing, images: imgs })}
              multiple
            />

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
                <span className="tracking-eyebrow text-[0.72rem]">Activo</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} />
                <span className="tracking-eyebrow text-[0.72rem]">Destacado</span>
              </label>
            </div>

            <div className="flex gap-3 pt-4 border-t border-[var(--eafg-line)]">
              <button type="submit" className="bg-[var(--eafg-copper)] text-white px-6 py-3 tracking-eyebrow" data-testid="pf-save">
                {t("admin.save")}
              </button>
              <button type="button" onClick={() => setEditing(null)} className="border border-[var(--eafg-ink)] px-6 py-3 tracking-eyebrow">
                {t("admin.cancel")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", step, required, testId, textarea = false }) {
  return (
    <label className="block">
      <span className="tracking-eyebrow text-[var(--eafg-muted)] block mb-2">{label}</span>
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3}
          className="w-full border border-[var(--eafg-line)] p-2 bg-transparent" data-testid={testId} />
      ) : (
        <input type={type} step={step} required={required} value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border-b border-[var(--eafg-line)] py-2 bg-transparent" data-testid={testId} />
      )}
    </label>
  );
}
